'use client';

import {Alert} from 'common/components/alert';
import {
  getResumeUploadUrl,
  getResumeStatus,
  processResume,
  uploadResumeToS3,
} from 'iq-onboarding/api';
import type {ResumeSuggestions} from 'iq-onboarding/types';
import {
  trackResumeFailed,
  trackResumeInvalid,
  trackResumeProcessed,
  trackResumeSelected,
  trackResumeSkipped,
  trackResumeUploadStarted,
} from 'iq-onboarding/tracking';
import {MAX_RESUME_BYTES, type ResumeFileInfo, validateResumeFile} from 'iq-onboarding/utils';
import {Loader2, Upload, X} from 'lucide-react';
import {useCallback, useEffect, useRef, useState} from 'react';

export type ResumeCompleteResult = {
  resumeUuid: string;
  suggestions: ResumeSuggestions;
} | null;

type Phase =
  | {name: 'idle'}
  | {name: 'uploading'}
  | {name: 'processing'; resumeUuid: string}
  | {name: 'error'; message: string};

interface ResumeUploaderProps {
  onComplete: (result: ResumeCompleteResult) => void;
  // Fired once a valid file is selected (drag/drop or picker), before upload.
  onFileSelected?: () => void;
}

export function ResumeUploader({onComplete, onFileSelected}: ResumeUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [phase, setPhase] = useState<Phase>({name: 'idle'});
  const [file, setFile] = useState<File | null>(null);
  const [fileInfo, setFileInfo] = useState<ResumeFileInfo | null>(null);
  const [dragging, setDragging] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (phase.name !== 'processing') return;
    const {resumeUuid} = phase;

    intervalRef.current = setInterval(async () => {
      try {
        const status = await getResumeStatus(resumeUuid);
        if (status.status === 'completed') {
          clearInterval(intervalRef.current!);
          const {positions, companies, first_name, last_name, email} = status.suggestions;
          void trackResumeProcessed(
            positions.length,
            companies.length,
            !!(first_name || last_name || email),
          );
          onComplete({resumeUuid, suggestions: status.suggestions});
        } else if (status.status === 'failed') {
          clearInterval(intervalRef.current!);
          const message = status.error_message ?? 'Processing failed. Please try again.';
          void trackResumeFailed('processing', message);
          setPhase({name: 'error', message});
        }
      } catch {
        clearInterval(intervalRef.current!);
        const message = 'Could not check processing status. Please try again.';
        void trackResumeFailed('status_check', message);
        setPhase({name: 'error', message});
      }
    }, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase.name]);

  const handleFile = useCallback(
    async (f: File) => {
      setValidationError(null);
      if (f.size > MAX_RESUME_BYTES) {
        setValidationError('Your resume is too large. Please upload a PDF under 20MB.');
        setFile(null);
        setFileInfo(null);
        void trackResumeInvalid('too_large');
        return;
      }
      const info = await validateResumeFile(f);
      if (!info) {
        setValidationError('Please upload a valid PDF file.');
        setFile(null);
        setFileInfo(null);
        void trackResumeInvalid('invalid_pdf');
        return;
      }
      setFile(f);
      setFileInfo(info);
      void trackResumeSelected(f.size, info.ext);
      onFileSelected?.();
    },
    [onFileSelected],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragging(false);
      const dropped = e.dataTransfer.files[0];
      if (dropped) handleFile(dropped);
    },
    [handleFile],
  );

  async function handleContinue() {
    if (!file || !fileInfo) return;
    setPhase({name: 'uploading'});
    void trackResumeUploadStarted();
    try {
      const {resume_uuid, upload_url, fields} = await getResumeUploadUrl(fileInfo.ext);
      await uploadResumeToS3(upload_url, fields, file);
      await processResume(resume_uuid);
      setPhase({name: 'processing', resumeUuid: resume_uuid});
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Upload failed. Please try again.';
      void trackResumeFailed('upload', message);
      setPhase({name: 'error', message});
    }
  }

  // uploading and processing share a key — they show the same loading UI, no re-animation needed.
  const phaseKey =
    phase.name === 'uploading' || phase.name === 'processing' ? 'loading' : phase.name;

  function renderPhase() {
    if (phase.name === 'uploading' || phase.name === 'processing') {
      return (
        <div className='flex flex-col items-center gap-6 text-center py-16'>
          <Loader2 className='w-12 h-12 text-iq-orange animate-spin' />
          <div>
            <h1 className='text-3xl font-extrabold text-white mb-2'>
              {phase.name === 'uploading' ? 'Uploading your resume…' : 'Analyzing your resume…'}
            </h1>
            <p className='text-white/60 text-base'>
              Please wait while we configure your career preferences.
            </p>
          </div>
        </div>
      );
    }

    if (phase.name === 'error') {
      return (
        <div className='flex flex-col gap-6 text-center'>
          <div className='flex flex-col gap-4'>
            <h1 className='text-3xl font-extrabold text-white'>Something went wrong</h1>
            <Alert severity='error' variant='dark'>
              {phase.message}
            </Alert>
          </div>
          <div className='flex flex-col gap-3'>
            <button
              type='button'
              onClick={() => {
                setPhase({name: 'idle'});
                setFile(null);
                setFileInfo(null);
              }}
              className='self-center text-sm text-iq-orange hover:underline font-medium'>
              Try again
            </button>
            <button
              type='button'
              onClick={() => {
                void trackResumeSkipped('error');
                onComplete(null);
              }}
              className='self-center text-sm text-white/50 hover:text-white/80 transition-colors'>
              Continue without a resume
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className='flex flex-col gap-8'>
        <div className='text-center'>
          <h1 className='text-4xl font-extrabold text-white mb-3'>
            Sign up to <span className='text-iq-yellow'>Interview Query</span>
          </h1>
          <p className='text-white/60 text-base'>
            Upload your resume and we&apos;ll configure your profile automatically.
          </p>
        </div>

        <div
          onClick={() => !file && inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-2xl h-[152px] px-16 flex flex-col items-center justify-center gap-4 transition-colors ${
            dragging
              ? 'border-iq-orange bg-iq-orange/10 cursor-copy'
              : file
                ? 'border-green-400/60 bg-green-400/10 cursor-default'
                : 'border-white/20 hover:border-white/40 bg-white/5 hover:bg-white/10 cursor-pointer'
          }`}>
          <input
            ref={inputRef}
            type='file'
            accept='.pdf'
            data-testid='file-input'
            className='hidden'
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
            }}
          />

          {file ? (
            <div className='flex items-center gap-3'>
              <Upload className='w-5 h-5 text-green-400' />
              <span className='text-sm font-medium text-white'>{file.name}</span>
              <button
                type='button'
                onClick={(e) => {
                  e.stopPropagation();
                  setFile(null);
                  setFileInfo(null);
                }}
                className='text-white/50 hover:text-white transition-colors'
                aria-label='Remove file'>
                <X className='w-4 h-4' />
              </button>
            </div>
          ) : (
            <>
              <Upload className='w-10 h-10 text-white/40' />
              <div className='text-center'>
                <p className='text-base font-semibold text-white'>Drag & drop or click to upload</p>
                <p className='text-sm text-white/50 mt-1'>PDF only</p>
              </div>
            </>
          )}
        </div>

        {validationError && (
          <Alert severity='error' variant='dark'>
            {validationError}
          </Alert>
        )}

        <div className='flex flex-col gap-4'>
          <button
            type='button'
            disabled={!file}
            onClick={handleContinue}
            className='w-full py-4 px-8 bg-iq-orange text-white text-base font-bold rounded-full hover:opacity-90 transition-opacity disabled:opacity-30'>
            Continue
          </button>
          <button
            type='button'
            onClick={() => {
              void trackResumeSkipped('initial');
              onComplete(null);
            }}
            className='self-center text-sm text-white/50 hover:text-white/80 transition-colors text-center py-1'>
            Continue without a resume
          </button>
        </div>
      </div>
    );
  }

  return (
    <div key={phaseKey} className='animate-in fade-in slide-in-from-right-4 duration-300'>
      {renderPhase()}
    </div>
  );
}
